export type SeverityBand = 'healthy' | 'needs_attention' | 'critical';

// Mirrors apps/api/src/jobs/score-stage.ts SEVERITY_BAND_THRESHOLDS; the API's consistency test fails if they drift.
export const SEVERITY_BAND_THRESHOLDS = {
  healthy: 78,
  needs_attention: 55,
} as const;

/** The band a composite or dimension score earns. Inclusive on both cuts. */
export function severityBandForScore(score: number): SeverityBand {
  if (score >= SEVERITY_BAND_THRESHOLDS.healthy) return 'healthy';
  if (score >= SEVERITY_BAND_THRESHOLDS.needs_attention) return 'needs_attention';
  return 'critical';
}

/** LedgerRow vocabulary: ok / open / bad. */
export function ledgerToneForScore(score: number): 'ok' | 'open' | 'bad' {
  const band = severityBandForScore(score);
  return ledgerToneForSeverityBand(band);
}

/** LedgerRow vocabulary for a severity band already computed by the server. */
export function ledgerToneForSeverityBand(band: SeverityBand): 'ok' | 'open' | 'bad' {
  return band === 'healthy' ? 'ok' : band === 'needs_attention' ? 'open' : 'bad';
}

/** Stat-tile vocabulary: ok / warn / bad. */
export function statToneForScore(score: number): 'ok' | 'warn' | 'bad' {
  const band = severityBandForScore(score);
  return band === 'healthy' ? 'ok' : band === 'needs_attention' ? 'warn' : 'bad';
}

/** Dimension-row vocabulary: ok / attn / crit — the mapper's DimensionScoreRow.tone. */
export function dimensionToneForScore(score: number): 'ok' | 'attn' | 'crit' {
  const band = severityBandForScore(score);
  return band === 'healthy' ? 'ok' : band === 'needs_attention' ? 'attn' : 'crit';
}

export function ledgerToneForDimensionTone(tone: 'ok' | 'attn' | 'crit'): 'ok' | 'open' | 'bad' {
  return tone === 'ok' ? 'ok' : tone === 'attn' ? 'open' : 'bad';
}

export function scoreToneForSeverityBand(band: SeverityBand): 'ok' | 'open' | 'bad' {
  return ledgerToneForSeverityBand(band);
}

/** The band in words — the non-colour carrier. */
export const SEVERITY_BAND_LABEL: Record<SeverityBand, string> = {
  healthy: 'Healthy',
  needs_attention: 'Needs attention',
  critical: 'Critical',
};
